import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  NotificationEvent,
  NotificationTemplate,
  PutAdminV1StoresStoreIdNotificationTemplatesEventBody,
} from '@nucleus-commerce/api-client/admin';

import { notificationsApi } from '../api/notifications-api';

import { notificationKeys } from './notification-keys';

interface SaveVars {
  event: NotificationEvent;
  body: PutAdminV1StoresStoreIdNotificationTemplatesEventBody;
}

/** 422 on `subject` or `body` with `meta.unknown` / `meta.allowed` for unknown placeholders. */
export function useSaveTemplate(storeId: string, successMessage = 'notifications:toasts.saved') {
  const queryClient = useQueryClient();
  const key = notificationKeys.list(storeId);
  return useMutation({
    mutationFn: ({ event, body }: SaveVars) => notificationsApi.save(storeId, event, body),
    meta: { successMessage, silentError: true },
    onSuccess: (saved) =>
      queryClient.setQueryData<NotificationTemplate[]>(key, (rows) =>
        rows?.map((row) => (row.event === saved.event ? saved : row)),
      ),
  });
}

/**
 * The on/off switch is the one optimistic update: flipping a boolean is idempotent and the row
 * would otherwise lag behind the switch. Any wording override is sent back unchanged.
 */
export function useToggleTemplate(storeId: string) {
  const queryClient = useQueryClient();
  const key = notificationKeys.list(storeId);
  return useMutation({
    mutationFn: (template: NotificationTemplate) =>
      notificationsApi.save(storeId, template.event, {
        subject: template.override?.subject ?? null,
        body: template.override?.body ?? null,
        isEnabled: !template.isEnabled,
      }),
    meta: { silentError: true },
    onMutate: async (template) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<NotificationTemplate[]>(key);
      queryClient.setQueryData<NotificationTemplate[]>(key, (rows) =>
        rows?.map((row) =>
          row.event === template.event ? { ...row, isEnabled: !template.isEnabled } : row,
        ),
      );
      return { previous };
    },
    onError: (_error, _template, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** 404 when there is no override to drop. */
export function useResetTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: NotificationEvent) => notificationsApi.reset(storeId, event),
    meta: { successMessage: 'notifications:toasts.reset', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.list(storeId) }),
  });
}
